import "./style.css";
import "./condition/body-condition.css";
import {
  sampleMacroBiomeAt,
  sampleWorldRegionWeightsAt,
  type DeveloperSpeed,
  type DeveloperTeleportTargetId,
  type ClientDeveloperCommand,
} from "@endclock/shared";
import { setupDeveloperEnvironment } from "./dev/dev-ui.js";
import { EndclockGame, type FrameRate, type GameSettings, type GraphicsQuality } from "./game/game.js";
import { InteractionHud } from "./interaction/interaction-hud.js";
import { InventoryUi } from "./items/inventory-ui.js";
import { GuestGate } from "./guest/guest-gate.js";
import { setupPwa } from "./pwa.js";
import { setupWorldServerData } from "./hud/world-server-data.js";
import { setupEnvironmentHud } from "./hud/environment-hud.js";
import { setupControlHud } from "./hud/control-hud.js";

const $ = <T extends Element>(selector: string): T => {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`Missing UI element: ${selector}`);
  return element;
};

const pwa = setupPwa();

const parameters = new URLSearchParams(location.search);
const quality = parameters.get("quality");
const frameRate = parameters.get("fps");
const settings: GameSettings = {
  graphics: (["auto", "low", "medium", "high"].includes(quality ?? "") ? quality : "auto") as GraphicsQuality,
  frameRate: (["auto", "30", "60"].includes(frameRate ?? "") ? frameRate : "auto") as FrameRate,
};
const effectEnabled = (name: string): boolean => parameters.get(name) !== "0";
const atmosphereFlags = {
  lighting: effectEnabled("lighting"),
  fog: effectEnabled("fog"),
  grading: effectEnabled("grading"),
  particles: effectEnabled("particles"),
};
const aiGlowEnabled = effectEnabled("aiGlow");

const guestGate = new GuestGate($("#app"), !parameters.has("entry"));
void guestGate.enter().then(startGame);

function startGame(nickname: string): void {
  if (parameters.has("entry")) {
    const entryUrl = new URL(location.href);
    entryUrl.searchParams.delete("entry");
    history.replaceState(null, "", entryUrl);
  }
  let game: EndclockGame;
  try {
    game = new EndclockGame(
      $("#game-canvas"),
      $("#joystick"),
      $("#joystick-knob"),
      settings,
      atmosphereFlags,
      aiGlowEnabled,
      $<HTMLElement>("#interaction-action"),
    );
    game.network.onGuestEntryRequired = () => {
      const entryUrl = new URL(location.href);
      entryUrl.searchParams.set("entry", "1");
      location.replace(entryUrl.href);
    };
    game.network.setGuestNickname(nickname);
    game.start();
  } catch (error) {
    const fatal = $("#fatal-error") as HTMLElement;
    fatal.hidden = false;
    $("#fatal-message").textContent = error instanceof Error ? error.message : "Unknown graphics error";
    $("#fatal-retry").addEventListener("click", () => location.reload());
    throw error;
  }

  const connection = $("#connection-status") as HTMLElement;
  setupWorldServerData();
  setupEnvironmentHud(() => game.environmentSample());
  setupControlHud(game.input, $<HTMLElement>("#app"));
  game.input.setCameraMode(game.camera.mode);
  const cameraMode = $<HTMLButtonElement>("#camera-mode");
  game.onCameraModeChange = (mode) => {
    game.input.setCameraMode(mode);
    cameraMode.textContent = `CAMERA: ${mode === "tps" ? "TPS" : "LEGACY"}`;
    cameraMode.setAttribute("aria-pressed", String(mode === "tps"));
  };
  const toggleCamera = (): void => game.setCameraMode(game.camera.mode === "tps" ? "legacy" : "tps");
  cameraMode.addEventListener("click", toggleCamera);
  game.input.onCameraToggle = toggleCamera;
  if (parameters.get("camera") === "tps") game.setCameraMode("tps");
  const interactionHud = new InteractionHud(
    $("#interaction-hud"),
    $("#interaction-feedback"),
    $("#interaction-target-indicator"),
  );
  game.onInteractionState = (snapshot) => interactionHud.render(snapshot, game.camera.viewProjection, game.renderer.viewportWidth, game.renderer.viewportHeight);
  interactionHud.render(game.interaction.snapshot(), game.camera.viewProjection, game.renderer.viewportWidth, game.renderer.viewportHeight);
  game.onConnectionState = (state) => {
    connection.dataset.state = state;
    connection.querySelector("span")!.textContent = state.toUpperCase();
  };
  game.network.onUpdateRequired = () => pwa.requireUpdate();
  const inventoryUi = new InventoryUi(game.inventory, game.network, game.input);

  const developerState = setupDeveloperEnvironment(game);

  // Gesture ownership is scoped to gameplay surfaces; panels retain native scroll and text behavior.
  for (const surface of document.querySelectorAll<HTMLElement>("#game-canvas, .joystick, .crouch-control, .interaction-action")) {
    surface.addEventListener("touchmove", (event) => event.preventDefault(), { passive: false });
    surface.addEventListener("gesturestart", (event) => event.preventDefault(), { passive: false });
  }

  const debug = $("#debug-overlay") as HTMLElement;
  if (parameters.get("debug") === "1" || parameters.get("debugWorld") === "1") {
    debug.hidden = false;
    window.setInterval(() => {
      const value = game.snapshot();
      debug.textContent = [
        `AIM ${value.control.aimYaw.toFixed(3)} / ${value.control.aimPitch.toFixed(3)}  BODY ${value.control.bodyYaw.toFixed(3)}  ${value.control.stance}/${value.control.gait}/${value.control.motionMode}  ${value.control.pointerLock}`,
        `COMMANDS ${value.control.savedCommands}  HARD ${value.control.hardCorrections}  CAMERA ${value.control.cameraCpuMs.toFixed(2)}ms`,
        `FPS ${value.metrics.fps.toFixed(0)}  ${value.metrics.frameMs.toFixed(1)}ms  CPU ${value.metrics.cpuMs.toFixed(1)}ms`,
        `DRAW ${value.metrics.drawCalls}  TRI ${Math.round(value.metrics.triangles / 1_000)}k  TERRAIN ${Math.round(value.metrics.totalTerrainTriangles / 1_000)}k`,
        `CHUNKS ${value.metrics.visibleTerrainChunks}/${value.metrics.totalTerrainChunks}  WATER ${value.metrics.waterDrawCalls}`,
        `WORLD ${value.streaming.currentChunk.id}  LOAD ${value.streaming.loadedChunks}  READY ${value.streaming.readyActiveChunks}/${value.world.readyActiveChunks}  GPU ${value.streaming.gpuReadyChunks}  CPU ${value.streaming.cpuReadyChunks}  PEND ${value.streaming.pendingChunks}  ERR ${value.streaming.failedChunks}/${value.streaming.recoveredChunks}`,
        `STREAM GEN ${value.streaming.generationMsAverage.toFixed(2)}/${value.streaming.generationMsMax.toFixed(2)}ms  UP ${value.streaming.uploadMsAverage.toFixed(2)}/${value.streaming.uploadMsMax.toFixed(2)}ms  EVICT ${value.streaming.evictedChunks}`,
        `POS ${value.player.x.toFixed(1)}, ${value.player.z.toFixed(1)}  SEED ${value.world.seed}  GEN v${value.world.generationVersion}`,
        `BIOME ${value.world.currentBiome.primary}  SECONDARY ${value.world.currentBiome.secondary ?? "—"}  BORDER ${value.world.currentBiome.distanceToBoundary.toFixed(1)}m  TRANS ${(value.world.currentBiome.transition * 100).toFixed(0)}%`,
        `RADIATION ${value.world.radiation.intensity.toFixed(3)} ${value.world.radiation.level.toUpperCase()}  ECO ${value.world.ecology.protectionLevel.toFixed(3)}  MON ${value.world.ecology.monitoringCoverage.toFixed(3)}`,
        `NEAREST ${value.world.nearestPoi ?? "—"}`,
        `REG ASH ${value.world.currentRegions.ashForest.toFixed(2)}  RIVER ${value.world.currentRegions.riverValley.toFixed(2)}  FARM ${value.world.currentRegions.deadFarmland.toFixed(2)}  SUB ${value.world.currentRegions.ruinedSuburb.toFixed(2)}`,
        `REG CRATER ${value.world.currentRegions.craterField.toFixed(2)}  IND ${value.world.currentRegions.industrialWasteland.toFixed(2)}  AI ${value.world.currentRegions.aiExcavationZone.toFixed(2)}  WET ${value.world.currentRegions.wetwareZone.toFixed(2)}`,
        `POI H ${value.world.nearbyHumanSites.length}  AI ${value.world.nearbyAiSites.length}  STATIC ${value.world.stableObjectCount}  WCACHE ${value.world.cachedChunks}/${value.world.activeChunks}  COLL ${value.world.collisionCacheChunks}  FAIL ${value.world.failedChunks}`,
        `ROCKS ${value.metrics.visibleGeologyInstances}/${value.metrics.totalGeologyInstances}  GDRAW ${value.metrics.geologyDrawCalls}  GTRI ${value.metrics.geologyTriangles}/${value.metrics.totalGeologyTriangles}`,
        `TREES ${value.metrics.visibleVegetationTrees}/${value.metrics.totalVegetationTrees}  SHRUBS ${value.metrics.visibleVegetationShrubs}/${value.metrics.totalVegetationShrubs}  VDRAW ${value.metrics.vegetationDrawCalls}`,
        `VTRI ${value.metrics.vegetationTriangles}/${value.metrics.totalVegetationTriangles}  COLL ${value.metrics.collisionCandidatesAverage.toFixed(1)}/${value.metrics.collisionCandidatesMax}`,
        `HUMAN ${value.metrics.visibleHumanObjects}/${value.metrics.totalHumanObjects}  HDRAW ${value.metrics.humanDrawCalls}  HTRI ${value.metrics.humanTriangles}/${value.metrics.totalHumanTriangles}`,
        `AI ${value.metrics.visibleAiObjects}/${value.metrics.totalAiObjects}  AIDRAW ${value.metrics.aiDrawCalls}  AITRI ${value.metrics.aiTriangles}/${value.metrics.totalAiTriangles}  GLOW ${value.ai.glowEnabled ? "ON" : "OFF"}`,
        `PLAYER REP ${value.character.representation}  CHAR ${value.metrics.visibleCharacters}/${value.metrics.totalCharacters}  CDRAW ${value.metrics.characterDrawCalls}  CTRI ${value.metrics.characterTriangles}`,
        `CHAR ${value.character.localAnimation} ${value.character.localSpeed.toFixed(2)}m/s  JOINTS ${value.metrics.characterJoints}  JUP ${value.metrics.characterJointUpdates}  ANIM ${value.metrics.characterAnimationCpuMs.toFixed(2)}ms`,
        `ATM ${value.metrics.atmosphereDrawCalls}  ASH ${value.metrics.particleCount}  FOG ${value.atmosphere.flags.fog ? "ON" : "OFF"}  LIGHT ${value.atmosphere.flags.lighting ? "ON" : "OFF"}`,
        `RENDER ${value.metrics.width}×${value.metrics.height}  DPR ${devicePixelRatio.toFixed(1)}`,
        `WS ${value.network.state}  PING ${value.network.ping.toFixed(0)}ms`,
        `MESH ${(value.meshBytes / 1_048_576).toFixed(2)} MiB  INST ${(value.metrics.instanceBufferBytes / 1_024).toFixed(0)} KiB  ALLOC ${value.metrics.instanceBufferAllocatedBytesThisFrame}`,
        `ASSETS G:${value.geology.state.toUpperCase()} V:${value.vegetation.state.toUpperCase()} H:${value.human.state.toUpperCase()} AI:${value.ai.state.toUpperCase()} C:${value.character.state.toUpperCase()}  TEX ${(value.metrics.textureBytes / 1_024).toFixed(0)} KiB`,
        ...(value.streaming.lastError ? [`TERRAIN ERROR ${value.streaming.lastError}`] : []),
        ...(value.world.lastError ? [`WORLD ERROR ${value.world.lastError}`] : []),
      ].join("\n");
    }, 250);
  }


  window.__ENDCLOCK_TEST__ = {
    snapshot: () => game.snapshot(),
    terrainHeightAt: (x, z) => game.terrain.getHeightAt(x, z),
    enterWorld: () => game.enterWorld(),
    dropConnection: () => game.network.forceReconnect(),
    profileAt: (x, z, zoom) => game.profileAt(x, z, zoom),
    setAiGlowEnabled: (enabled) => game.setAiGlowEnabled(enabled),
    setCharacterBenchmark: (count) => game.setCharacterBenchmark(count),
    authorizeDeveloper: (token) => game.authorizeDeveloper(token),
    developerTeleport: (target, zoom) => game.teleportDeveloper(target, zoom),
    developerNoclip: (enabled) => game.setDeveloperNoclip(enabled),
    developerFreeFly: (enabled) => game.setDeveloperFreeFly(enabled),
    developerVertical: (value) => game.input.setDeveloperVertical(value),
    developerSpeed: (speed) => game.setDeveloperSpeed(speed),
    developerGameplayCommand: (command) => game.network.developerGameplayCommand(command),
    developerObserveBattle: (actorId, side, orbit) => game.observeBattle(actorId, side, orbit),
    developerLookAtBattleActor: (actorId) => game.lookAtBattleActor(actorId),
    toggleInventory: () => inventoryUi.toggle(),
    profileInventoryRender: (iterations) => inventoryUi.profileRender(iterations),
    inventorySnapshot: () => createInventoryBrowserSnapshot(game),
    setDeveloperPanelVisible: (visible) => {
      if (!game.network.developer.authorized) return;
      developerState.panelOpen = visible;
      ($("#developer-panel") as HTMLElement).hidden = !visible;
      ($("#developer-panel-open") as HTMLElement).hidden = visible;
    },
    worldRegionsAt: (x, z) => sampleWorldRegionWeightsAt(x, z),
    worldBiomeAt: (x, z) => sampleMacroBiomeAt(x, z),
  };

}

declare global {
  interface Window {
    __ENDCLOCK_TEST__: {
      snapshot: () => ReturnType<EndclockGame["snapshot"]>;
      terrainHeightAt: (x: number, z: number) => number;
      enterWorld: () => void;
      dropConnection: () => void;
      profileAt: (x: number, z: number, zoom?: number) => void;
      setAiGlowEnabled: (enabled: boolean) => void;
      setCharacterBenchmark: (count: number) => void;
      authorizeDeveloper: (token: string) => void;
      developerTeleport: (target: DeveloperTeleportTargetId, zoom?: number) => void;
      developerNoclip: (enabled: boolean) => void;
      developerFreeFly: (enabled: boolean) => void;
      developerVertical: (value: -1 | 0 | 1) => void;
      developerSpeed: (speed: DeveloperSpeed) => void;
      developerGameplayCommand: (command: ClientDeveloperCommand) => void;
      developerObserveBattle: (actorId?: number, side?: boolean, orbit?: number) => void;
      developerLookAtBattleActor: (actorId: number) => void;
      setDeveloperPanelVisible: (visible: boolean) => void;
      toggleInventory: () => void;
      profileInventoryRender: (iterations?: number) => ReturnType<InventoryUi["profileRender"]>;
      inventorySnapshot: () => ReturnType<typeof createInventoryBrowserSnapshot>;
      worldRegionsAt: (x: number, z: number) => ReturnType<typeof sampleWorldRegionWeightsAt>;
      worldBiomeAt: (x: number, z: number) => ReturnType<typeof sampleMacroBiomeAt>;
    };
  }
}

function createInventoryBrowserSnapshot(value: EndclockGame) {
  const snapshot = value.inventory.snapshot();
  return {
    physical: snapshot.physical,
    physicalResult: snapshot.physicalResult,
    physicalPending: snapshot.physicalPending,
    playerInventory: snapshot.playerInventory ? {
      ...snapshot.playerInventory,
      slots: snapshot.playerInventory.slots.map((stack) => stack ? { ...stack } : null),
    } : null,
    container: snapshot.container ? {
      accessSessionId: snapshot.container.accessSessionId,
      state: {
        ...snapshot.container.state,
        slots: snapshot.container.state.slots.map((stack) => stack ? { ...stack } : null),
      },
    } : null,
    worldDrops: [...snapshot.worldDrops.values()].map((entity) => ({
      ...entity,
      stack: { ...entity.stack },
    })),
    pendingRequests: [...snapshot.pendingRequests].map(([requestId, action]) => ({ requestId, action })),
    results: [...snapshot.results.values()].map((result) => ({ ...result })),
    lastResult: snapshot.lastResult ? { ...snapshot.lastResult } : null,
    lastContainerCloseReason: snapshot.lastContainerCloseReason,
    inventoryResyncPending: snapshot.inventoryResyncPending,
    containerResyncPending: snapshot.containerResyncPending,
  };
}
